/**
 * 글꼴 선택. 저장과 적용. (3.6 내 정보)
 *
 * theme.ts 와 같은 구조다 — localStorage 에 담고 html 요소에 표시를 남기면
 * CSS 가 나머지를 한다. 이유도 같다.
 *
 *   서버는 글꼴을 알 필요가 없다. SSR 이 없고 서버가 HTML 을 만들지 않는다.
 *   쿠키로 하면 매 요청 헤더에 실려 나가기만 하고 얻는 것이 없다.
 *   "단말기별로 남는다" 는 것이 오히려 맞다 — PC 와 폰의 화면 크기가 달라
 *   같은 글꼴이 양쪽에서 똑같이 좋으리라는 보장이 없다.
 *
 * 🔴 테마와 다른 점 하나 — 글꼴은 공짜가 아니다
 *   다크 모드는 색만 바뀌므로 전환 비용이 0 이다.
 *   글꼴은 고르는 순간 그 글꼴 파일을 받아야 한다.
 *   그래서 선택지를 늘릴 때는 "이 글꼴 파일을 언제 받는가" 를 함께 정해야 한다.
 *   지금 두 선택지는 둘 다 추가 다운로드가 없다 —
 *   Pretendard 는 이미 기본으로 받고 있고, 시스템은 받을 것이 없다.
 */

export type FontChoice = 'pretendard' | 'system';

/** theme-init.js 와 같은 키를 쓴다. 바꾸려면 양쪽을 함께 고친다. */
export const FONT_STORAGE_KEY = 'font';

const CHOICES: FontChoice[] = ['pretendard', 'system'];

/**
 * 기본값이 pretendard 인 이유.
 *
 * 시스템 글꼴은 기기마다 다르다. Windows 는 영문·숫자에 Segoe UI 가 잡히는데
 * 여기에 한글 글리프가 없어 한글만 맑은 고딕으로 떨어진다. 한 줄 안에서
 * 두 글꼴이 섞이고, 아이폰에서는 또 다르게 보인다.
 *
 * 숫자를 세로로 비교하는 화면이 많아 기기마다 다른 것이 특히 불리하다.
 */
const DEFAULT_CHOICE: FontChoice = 'pretendard';

/** 저장된 설정을 읽는다. 없거나 이상한 값이면 기본값이다. */
export function readFontChoice(): FontChoice {
  try {
    const saved = localStorage.getItem(FONT_STORAGE_KEY);
    return CHOICES.includes(saved as FontChoice) ? (saved as FontChoice) : DEFAULT_CHOICE;
  } catch {
    // 사파리 프라이빗 모드 등에서 localStorage 접근이 막힐 수 있다.
    // 글꼴 때문에 앱이 죽으면 안 된다. (theme.ts 와 같은 처리)
    return DEFAULT_CHOICE;
  }
}

/**
 * html 요소에 data-font 를 남긴다. CSS 가 이 값을 보고 font-family 를 고른다.
 *
 * 🔴 기본값일 때는 속성을 지운다
 *   남겨두면 CSS 가 html[data-font='pretendard'] 규칙을 따로 가져야 하고,
 *   그러면 같은 글꼴 스택이 두 곳에 적히게 된다. 하나만 고치면 어긋난다.
 *   기본은 html 규칙이 맡고, 벗어난 경우에만 속성이 붙는다.
 */
export function applyFont(choice: FontChoice): void {
  if (choice === DEFAULT_CHOICE) {
    document.documentElement.removeAttribute('data-font');
  } else {
    document.documentElement.dataset.font = choice;
  }
}

/** 설정을 저장하고 즉시 적용한다. */
export function saveFontChoice(choice: FontChoice): void {
  try {
    localStorage.setItem(FONT_STORAGE_KEY, choice);
  } catch {
    // 저장에 실패해도 이번 세션 동안은 적용되게 한다.
  }
  applyFont(choice);
}

/** 화면에 보여줄 이름과 설명. 선택지를 늘리면 여기와 CHOICES, index.css 를 함께 고친다. */
export const FONT_LABELS: Record<FontChoice, { name: string; note: string }> = {
  pretendard: {
    name: 'Pretendard',
    note: '한글과 숫자가 같은 글꼴이라 표에서 자릿수가 잘 맞습니다. 기본값입니다.',
  },
  system: {
    name: '기기 기본 글꼴',
    note: '아이폰은 애플 SD 고딕, Windows 는 맑은 고딕으로 보입니다. 기기마다 다릅니다.',
  },
};
